import React, { useCallback, useState } from 'react';
import classNames from '@utils/classnames';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { ArrowSquareOutIcon, CalendarDotsIcon, ClockIcon } from '@phosphor-icons/react';
import toast from 'react-hot-toast';

import { PageHeader } from 'components/PageHeader';
import { OrderLineStopType, SelfBillKind, SupplierSelfBillStatus } from 'src/types/enums';
import {
  useSelfBill,
  approveSelfBill as approveSelfBillRequest,
  discardSelfBillDraft as discardSelfBillDraftRequest,
  excludeOrderFromSelfBill as excludeOrderFromSelfBillRequest,
  generateSelfBill as generateSelfBillRequest,
  removeOrderFromSelfBill as removeOrderFromSelfBillRequest,
  refreshSelfBillPeppolStatus,
  retrySelfBill as retrySelfBillRequest,
  sendSelfBill as sendSelfBillRequest,
  sendSelfBillViaPeppol,
} from '../models';
import { Pill, PillColor } from 'components/pill/Pill';
import { invariant, nullthrows } from '@utils/invariant';

import { Breadcrumb } from '../../../components/Breadcrumb';
import { DataField } from '../../../components/DataField';
import { DelayedSpinner } from '../../../components/Spinner';
import { formatDate, formatDateTime, formatInputTime } from '../../../utils/date';
import { getInvoiceTotals } from '../../../domain/invoice-totals';
import { formatIban, isValidIban } from '@utils/iban';
import { selfBillChannelsFromSupplier } from '../../supplier/self-bill-delivery';
import { INVOICE_CHANNEL_LABELS } from '../../customer/document-delivery';
import { extractIntFromText, decimateAndFormatNumber } from '../../../utils/number';
import { FileUploaderButton } from '../../document/components/FileUploaderButton';
import { ContactsDialog } from '../../../components/dialog/ContactsDialog';
import { getDisplayError } from '../../../utils/get-display-error';
import { ConfirmDialog } from '../../../components/dialog/ConfirmDialog';
import { PageHeading } from 'components/PageHeading';
import { SummaryBar } from 'components/SummaryBar';
import { TotalsCard } from 'components/TotalsCard';
import { captureException } from '@sentry/react';
import { trackEvent } from '../../../utils/track-event';
import { Alert } from '../../../components/alert/Alert';
import { PeppolStatusCard } from '../../peppol/components/PeppolStatusCard';
import { AuditLogsList } from '../../audit-log/components/AuditLogsList';
import { selfBillPath } from '../paths';
import { SELF_BILL_KIND_CONFIG } from './self-bill-kind-config';

const SELF_BILL_STATUS_COLORS: Record<SupplierSelfBillStatus, PillColor> = {
  [SupplierSelfBillStatus.Draft]: 'primary',
  [SupplierSelfBillStatus.Sent]: 'positive',
  [SupplierSelfBillStatus.Failed]: 'negative',
};

const SELF_BILL_STATUS_LABELS: Record<SupplierSelfBillStatus, string> = {
  [SupplierSelfBillStatus.Draft]: 'Concept',
  [SupplierSelfBillStatus.Sent]: 'Verzonden',
  [SupplierSelfBillStatus.Failed]: 'Mislukt',
};

export interface ISelfBillPageProps {
  kind: SelfBillKind;
}

const SelfBillPage: React.FC<ISelfBillPageProps> = ({ kind }) => {
  const navigate = useNavigate();
  const { selfBillId } = useParams<{ selfBillId: string }>();
  invariant(selfBillId);
  const { data, isLoading, mutate } = useSelfBill(selfBillId);
  // Bumped on every refetch so the history list reloads too.
  const [historyVersion, setHistoryVersion] = useState(0);
  const refetch = useCallback(() => {
    setHistoryVersion((v) => v + 1);
    return mutate();
  }, [mutate]);

  if (isLoading) {
    return <DelayedSpinner size={8} />;
  }

  const selfBill = nullthrows(data, 'Self billing not found');
  // Older links (emails, notifications) can point at the other page.
  if (selfBill.kind !== kind) {
    return <Navigate to={selfBillPath(selfBill.kind, selfBill.id)} replace />;
  }
  const config = SELF_BILL_KIND_CONFIG[kind];
  const linkedPurchase = selfBill.linkedPurchase;

  const handleApprove = async (andSend: boolean) => {
    try {
      const outcome = await approveSelfBillRequest(selfBill.id, andSend);
      if (outcome.status === SupplierSelfBillStatus.Failed) {
        throw new Error(outcome.error ?? 'onbekende fout');
      }
      trackEvent('self_bill_approved', {
        self_bill_id: selfBill.id,
        and_send: andSend,
        invoice_number: outcome.invoiceNumber ?? null,
      });
      toast.success(
        andSend
          ? `${config.documentName} goedgekeurd & verzonden (${outcome.invoiceNumber ?? ''})`
          : `${config.documentName} goedgekeurd (${outcome.invoiceNumber ?? ''})`,
      );
      refetch();
    } catch (err) {
      captureException(err);
      toast.error(`Kon ${config.documentWithArticle} niet goedkeuren: ${getDisplayError(err)}`);
      // A failed send still assigns the number and flips the status to Failed.
      refetch();
    }
  };
  const purchaseLines = selfBill.orders
    .map((o) =>
      o.lines
        .map((l) =>
          l.purchases.map((p) => {
            return {
              ...p,
              vatRateId: p.vatRate.id,
            };
          }),
        )
        .flat(),
    )
    .flat();
  const billTotals = getInvoiceTotals(purchaseLines, selfBill.cashDiscountPercentage ?? undefined);
  // A ride overview never goes out over Peppol, whatever the carrier's channels say.
  const sendChannels = selfBillChannelsFromSupplier(selfBill.supplier).filter(
    (channel) => config.hasPeppol || channel !== 'peppol',
  );
  const cashDiscount = billTotals.cashDiscount;
  return (
    <>
      <PageHeader title={`${config.documentName} ${selfBill.entryNumber}`} />

      <PageHeading
        noMarginBottom
        leftSide={
          <Breadcrumb
            parentItem={{
              name: config.title,
              to: config.listPath,
            }}
            currentItem={selfBill.entryNumber}
          />
        }
        rightSide={
          <div className="flex gap-2">
            {selfBill.status === SupplierSelfBillStatus.Draft && (
              <>
                <ConfirmDialog
                  triggerText="Verwerp"
                  triggerColor="danger"
                  title="Concept verwerpen?"
                  submitText="Verwerp"
                  description="Het concept wordt verwijderd en de onderliggende orders komen terug in aanmerking voor een volgende run."
                  onSubmit={async () => {
                    try {
                      await discardSelfBillDraftRequest(selfBill.id);
                      trackEvent('self_bill_discarded', { self_bill_id: selfBill.id });
                      toast.success('Concept verwijderd');
                      navigate(config.listPath);
                    } catch (err) {
                      captureException(err);
                      toast.error(`Kon concept niet verwerpen: ${getDisplayError(err)}`);
                    }
                  }}
                />
                <ConfirmDialog
                  triggerText="Goedkeuren"
                  triggerColor="primary"
                  title={`${config.documentName} goedkeuren?`}
                  submitText="Goedkeuren & verzenden"
                  secondarySubmitText="Goedkeuren"
                  description={
                    config.createsPurchaseOnApproval
                      ? `Er wordt een factuurnummer toegekend en een aankoopfactuur aangemaakt. Met "Goedkeuren & verzenden" gaat de self billing ook naar ${selfBill.supplier.name} (Peppol/e-mail).`
                      : `Er wordt een nummer toegekend. Met "Goedkeuren & verzenden" gaat het ritoverzicht ook per e-mail naar ${selfBill.supplier.name}.`
                  }
                  onSubmit={() => handleApprove(true)}
                  onSecondarySubmit={() => handleApprove(false)}
                />
              </>
            )}

            {selfBill.status === SupplierSelfBillStatus.Failed && (
              <ConfirmDialog
                triggerText="Opnieuw proberen"
                triggerColor="primary"
                title={`${config.documentName} opnieuw versturen?`}
                submitText="Opnieuw proberen"
                description={
                  config.hasPeppol
                    ? 'Het factuurnummer blijft behouden. E-mail en Peppol worden opnieuw aangeboden.'
                    : 'Het nummer blijft behouden. De e-mail wordt opnieuw aangeboden.'
                }
                onSubmit={async () => {
                  try {
                    const outcome = await retrySelfBillRequest(selfBill.id);
                    if (outcome.status === SupplierSelfBillStatus.Failed) {
                      throw new Error(outcome.error ?? 'onbekende fout');
                    }
                    trackEvent('self_bill_retry_send', { self_bill_id: selfBill.id });
                    toast.success(`${config.documentName} opnieuw verstuurd`);
                  } catch (err) {
                    captureException(err);
                    toast.error(`Kon ${config.documentWithArticle} niet opnieuw versturen: ${getDisplayError(err)}`);
                  } finally {
                    refetch();
                  }
                }}
              />
            )}

            {selfBill.status === SupplierSelfBillStatus.Sent && (
              <ContactsDialog
                triggerText="Verzend e-mail"
                triggerColor="primary"
                title={`${config.documentName} per e-mail versturen`}
                submitText="Verzend"
                description={`${config.documentName} naar ${selfBill.supplier?.name} versturen?`}
                contacts={selfBill.supplier.contacts.map((c) => {
                  return {
                    id: '' + c.id,
                    name: c.name,
                    email: c.email,
                  };
                })}
                initialValue={selfBill.supplier.contacts
                  .filter((v) => v.shouldReceiveSelfBillings)
                  .map((v) => '' + v.id)}
                onSubmit={async (contacts) => {
                  try {
                    await sendSelfBillRequest(
                      selfBill.id,
                      contacts.map((c) => +c),
                    );
                    trackEvent('self_bill_sent', {
                      self_bill_id: selfBill.id,
                      supplier_id: selfBill.supplier.id,
                      contact_count: contacts.length,
                    });
                    toast.success(`${config.documentName} verstuurd`);
                    refetch();
                  } catch (err: any) {
                    captureException(err);
                    toast.error(`Kon ${config.documentWithArticle} niet versturen: ${getDisplayError(err)}`);
                    throw err;
                  }
                }}
              />
            )}

            <ConfirmDialog
              triggerText="Genereer opnieuw"
              title="Pdf opnieuw genereren?"
              submitText="Genereer"
              description={`De pdf van ${config.documentWithArticle} wordt opnieuw gegenereerd.`}
              onSubmit={async () => {
                try {
                  await generateSelfBillRequest(selfBill.id);
                  trackEvent('self_bill_generated', { self_bill_id: selfBill.id });
                  toast.success('Pdf gegenereerd');
                } catch (err) {
                  captureException(err);
                  toast.error(`Kon pdf niet genereren: ${getDisplayError(err)}`);
                }
              }}
            />

            <FileUploaderButton buttonText="PDF" title={config.documentName} file={selfBill.document} disableUpload />
          </div>
        }
      />

      <div className="flex flex-col gap-4 p-7">
        {selfBill.generatedTotalExclVat !== billTotals.totalExclVat && (
          <Alert type="error">
            {`Totaal op pdf komt niet meer overeen met waarden op deze pagina: Totaal op PDF €${decimateAndFormatNumber(
              selfBill.generatedTotalExclVat,
              2,
              {
                decimalSeperator: ',',
              },
            )}, totaal op deze pagina €${decimateAndFormatNumber(billTotals.totalExclVat, 2, {
              decimalSeperator: ',',
            })}.`}
          </Alert>
        )}

        {config.requiresIban &&
          selfBill.status === SupplierSelfBillStatus.Draft &&
          !isValidIban(selfBill.supplier.iban) && (
            <Alert type="error">
              Deze vervoerder heeft geen geldige IBAN. Goedkeuren kan pas na een goedgekeurde wijziging van de
              bankrekening.
            </Alert>
          )}

        {config.requiresIban && selfBill.supplier.hasPendingBankAccountChange && (
          <Alert type="warning">
            Er staat een wijziging van de bankrekening klaar ter goedkeuring. Tot dan blijft de huidige IBAN gelden.
          </Alert>
        )}

        {selfBill.status === SupplierSelfBillStatus.Failed &&
          selfBill.peppolError &&
          selfBill.peppolError !== selfBill.peppol.detail && (
            <Alert type="error">{`Versturen mislukt: ${selfBill.peppolError}`}</Alert>
          )}

        <SummaryBar>
          <DataField title="Vervoerder">
            {selfBill.supplier.name} ({selfBill.supplier.id})
          </DataField>
          <DataField title="Datum">{formatDate(selfBill.invoiceDate ?? selfBill.createdAt)}</DataField>
          <DataField title="Ref">{selfBill.entryNumber}</DataField>
          <DataField title="IBAN">{formatIban(selfBill.supplier.iban) || '-'}</DataField>
          <DataField title="Verzenden via">
            {['Carrier portal', ...sendChannels.map((channel) => INVOICE_CHANNEL_LABELS[channel])].join(', ')}
          </DataField>
          {selfBill.invoiceNumber && <DataField title={config.numberLabel}>{selfBill.invoiceNumber}</DataField>}
          {selfBill.status !== SupplierSelfBillStatus.Draft && (
            <DataField title="E-mail verzonden">
              {selfBill.emailSentAt ? formatDateTime(selfBill.emailSentAt) : 'Nee'}
            </DataField>
          )}
          <DataField title="Status">
            <Pill color={SELF_BILL_STATUS_COLORS[selfBill.status]} size="sm">
              {SELF_BILL_STATUS_LABELS[selfBill.status]}
            </Pill>
          </DataField>
          {linkedPurchase && (
            <DataField title="Aankoopfactuur">
              <Link
                to={`/internal/finance/purchases/${linkedPurchase.id}`}
                className="link-text flex items-center gap-1"
              >
                {linkedPurchase.invoiceNumber}
                <ArrowSquareOutIcon className="w-4 h-4" />
              </Link>
            </DataField>
          )}
        </SummaryBar>

        {config.hasPeppol && selfBill.status !== SupplierSelfBillStatus.Draft && (
          <PeppolStatusCard
            peppol={selfBill.peppol}
            blockReason={selfBill.peppolAvailability}
            onSend={() => sendSelfBillViaPeppol(selfBill.id)}
            onRefresh={() => refreshSelfBillPeppolStatus(selfBill.id)}
            onChanged={refetch}
          />
        )}

        <>
          {selfBill.orders
            .sort((a, b) => extractIntFromText(a.orderNumber || '') - extractIntFromText(b.orderNumber || ''))
            .map((order) => {
              const orderTotals = getInvoiceTotals(
                order.lines
                  .map((l) =>
                    l.purchases.map((p) => {
                      return {
                        ...p,
                        vatRateId: p.vatRate.id,
                      };
                    }),
                  )
                  .flat(),
              );

              const isLastOrder = selfBill.orders.length === 1;

              const removeOrder = async () => {
                try {
                  await removeOrderFromSelfBillRequest(selfBill.id, order.id);
                  toast.success(`Order ${order.orderNumber} verwijderd uit ${config.documentWithArticle}`);
                  refetch();
                } catch (err) {
                  captureException(err);
                  toast.error(`Kon order niet verwijderen: ${getDisplayError(err)}`);
                  throw err;
                }
              };

              const excludeOrder = async () => {
                try {
                  await excludeOrderFromSelfBillRequest(selfBill.id, order.id);
                  toast.success(`Order ${order.orderNumber} permanent verwijderd uit ${config.documentWithArticle}`);
                  if (isLastOrder) {
                    navigate(config.listPath);
                  } else {
                    refetch();
                  }
                } catch (err) {
                  captureException(err);
                  toast.error(`Kon order niet verwijderen: ${getDisplayError(err)}`);
                  throw err;
                }
              };

              return (
                <div className="card" key={`order-${order.orderNumber}`}>
                  <div className="card-heading">
                    <Link to={`/internal/orders/${order.id}/general`} className="link-text">
                      Order {order.orderNumber}
                    </Link>
                    {selfBill.status === SupplierSelfBillStatus.Draft &&
                      (isLastOrder ? (
                        <ConfirmDialog
                          triggerText="Verwijder order"
                          triggerColor="danger"
                          title={`Order ${order.orderNumber} permanent verwijderen?`}
                          submitText="Permanent verwijderen"
                          description="Dit is de laatste order: het concept wordt verworpen en de order komt niet terug in een volgende run."
                          onSubmit={excludeOrder}
                        />
                      ) : (
                        <ConfirmDialog
                          triggerText="Verwijder order"
                          triggerColor="danger"
                          title={`Order ${order.orderNumber} verwijderen?`}
                          submitText="Verwijder"
                          secondarySubmitText="Permanent verwijderen"
                          description="Een verwijderde order komt terug in een volgende run, een permanent verwijderde niet. De pdf wordt opnieuw gegenereerd."
                          onSubmit={removeOrder}
                          onSecondarySubmit={excludeOrder}
                        />
                      ))}
                  </div>

                  <div className="card-content flex flex-col gap-3.5">
                    {order.lines.map((line) => {
                      return (
                        <div className="flex flex-col gap-3.5" key={`line-${line.id}`}>
                          <div className="flex flex-col gap-2">
                            {line.stops
                              .sort((a, b) => a.sequenceIndex - b.sequenceIndex)
                              .map((stop) => {
                                return (
                                  <div
                                    className="grid grid-cols-4 items-center gap-3 text-sm"
                                    key={`line-${line.id}-${stop.id}`}
                                  >
                                    <div className="font-semibold text-fg">
                                      {stop.type === OrderLineStopType.Load ? 'Laden' : 'Lossen'}
                                    </div>
                                    <div className="text-fg-secondary">
                                      {stop.location.country}-{stop.location.name}
                                    </div>

                                    <div className="flex items-center gap-1.5 text-fg-secondary">
                                      <CalendarDotsIcon className="h-3.5 w-3.5 text-fg-muted" />
                                      {formatDate(stop.date)}
                                    </div>
                                    <div className="flex items-center gap-1.5 text-fg-secondary">
                                      <ClockIcon className="h-3.5 w-3.5 text-fg-muted" />
                                      {`${formatInputTime(stop.timeStart)}-${formatInputTime(stop.timeEnd)}`}
                                    </div>
                                  </div>
                                );
                              })}
                          </div>

                          <div className="overflow-hidden rounded-md border border-border">
                            <div className="grid grid-cols-5 gap-x-4 border-b border-border bg-surface-sunken px-3.5 py-2">
                              <div className="label-text">Beschrijving</div>
                              <div className="label-text">Aantal</div>
                              <div className="label-text">Eenheidsprijs</div>
                              <div className="label-text">Totaal</div>
                              <div className="label-text">Btw</div>
                            </div>

                            <div className="grid grid-cols-5 gap-x-4 gap-y-1 px-3.5 py-2.5 text-sm">
                              {line.purchases.map((purchaseLine) => {
                                return (
                                  <React.Fragment key={`sale-${purchaseLine.id}`}>
                                    <div>{purchaseLine.productType.nameNl}</div>
                                    <div>
                                      {decimateAndFormatNumber(purchaseLine.amount, 2, {
                                        decimalSeperator: ',',
                                      })}
                                    </div>
                                    <div className="whitespace-nowrap">
                                      €{' '}
                                      {decimateAndFormatNumber(purchaseLine.unitPrice, 2, {
                                        decimalSeperator: ',',
                                      })}
                                    </div>
                                    <div className="whitespace-nowrap">
                                      €{' '}
                                      {decimateAndFormatNumber(
                                        (purchaseLine.amount * purchaseLine.unitPrice) / 100,
                                        2,
                                        {
                                          decimalSeperator: ',',
                                        },
                                      )}
                                    </div>
                                    <div
                                      className={classNames('whitespace-nowrap', {
                                        'text-negative-text':
                                          purchaseLine.vatRate.id !== selfBill.supplier.defaultVatRate.id,
                                      })}
                                    >
                                      {`€ ${decimateAndFormatNumber(
                                        (purchaseLine.amount *
                                          purchaseLine.unitPrice *
                                          purchaseLine.vatRatePercentage) /
                                          1000000,
                                        2,
                                        {
                                          decimalSeperator: ',',
                                        },
                                      )} - ${decimateAndFormatNumber(purchaseLine.vatRatePercentage, 2, {
                                        decimalSeperator: ',',
                                      })}%`}
                                    </div>

                                    {!!purchaseLine.externalNote && (
                                      <div className="col-span-5">
                                        <div className="text-sm">Extra informatie: {purchaseLine.externalNote}</div>
                                      </div>
                                    )}
                                  </React.Fragment>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      );
                    })}

                    <div className="whitespace-nowrap text-xs text-fg-muted">
                      {`Order totaal: € ${decimateAndFormatNumber(orderTotals.totalExclVat, 2, {
                        decimalSeperator: ',',
                      })}`}
                    </div>
                  </div>
                </div>
              );
            })}
        </>

        <TotalsCard
          rows={[
            {
              label: 'Totaal excl. btw:',
              value: `€ ${decimateAndFormatNumber(billTotals.totalExclVat, 2, { decimalSeperator: ',' })}`,
            },
            ...(cashDiscount
              ? [
                  {
                    label: `Korting voor contant (${decimateAndFormatNumber(cashDiscount.percentage * 100, 2, {
                      decimalSeperator: ',',
                    })}%):`,
                    value: `- € ${decimateAndFormatNumber(cashDiscount.discountAmount, 2, {
                      decimalSeperator: ',',
                    })}`,
                  },
                  {
                    label: 'Maatstaf van heffing:',
                    value: `€ ${decimateAndFormatNumber(billTotals.taxableBase, 2, { decimalSeperator: ',' })}`,
                  },
                ]
              : []),
            {
              label: 'Totaal btw:',
              value: `€ ${decimateAndFormatNumber(billTotals.payableVat, 2, { decimalSeperator: ',' })}`,
            },
            ...(cashDiscount
              ? [
                  {
                    label: 'Totaal met korting-contant:',
                    isEmphasised: true,
                    value: `€ ${decimateAndFormatNumber(billTotals.totalPayable, 2, { decimalSeperator: ',' })}`,
                  },
                  {
                    label: 'Totaal zonder korting-contant:',
                    value: `€ ${decimateAndFormatNumber(cashDiscount.totalWithoutDiscount, 2, { decimalSeperator: ',' })}`,
                  },
                ]
              : [
                  {
                    label: 'Totaal incl. btw:',
                    isEmphasised: true,
                    value: `€ ${decimateAndFormatNumber(billTotals.totalInclVat, 2, { decimalSeperator: ',' })}`,
                  },
                ]),
          ]}
        />

        <div>
          <h2 className="heading-two mb-2">Historiek</h2>
          <AuditLogsList key={selfBill.id} filter={{ supplierSelfBillId: selfBill.id }} refreshToken={historyVersion} />
        </div>
      </div>
    </>
  );
};

export default SelfBillPage;
